import AppTranslation from '@/entities/AppTranslation';
import OptionSet from '@/entities/OptionSet';
import AppText from 'entities/AppText';
import Language from 'entities/Language';

const MESSAGE_NAME = 'FLATTEN';
const POST_MESSAGE_TARGET_ORIGIN = '*';

const TIMEOUT = 2500;

export enum MESSAGE_TYPE {
  EditorAppId = 'EDITOR_APP_ID',
  EditorData = 'EDITOR_DATA',
  WriteTranslations = 'WRITE_TRANSLATIONS',
  CloseApplication = 'CLOSE_APPLICATION',
}

export enum MESSAGE_DATA_TYPE {
  Settings = 'SETTINGS',
  Database = 'DATABASE',
  OptionSet = 'OPTION_SETS',
}

function generateMessageId() {
  return Math.random().toString(36).substring(2);
}

interface RemoteMessageData {
  name: string;
  id: string;
  type: MESSAGE_TYPE;
  content: any;
}

async function editorMessage(type: MESSAGE_TYPE, content?: any): Promise<any> {
  return new Promise((resolve) => {
    const messageId: string = generateMessageId();
    const message = {
      name: MESSAGE_NAME,
      id: messageId,
      type: type,
      content: content,
    };

    function handleMessage(event: MessageEvent<RemoteMessageData>) {
      if (event.data.id === messageId) {
        window.removeEventListener('message', handleMessage);
        resolve(event.data.content);
      }
    }
    function handleTimeout() {
      window.removeEventListener('message', handleMessage);
      resolve(null);
    }
    window.addEventListener('message', handleMessage);
    setTimeout(handleTimeout, TIMEOUT);

    window.parent.postMessage(message, POST_MESSAGE_TARGET_ORIGIN);
  });
}

export async function closeApplication() {
  editorMessage(MESSAGE_TYPE.CloseApplication);
}

export async function writeTranslations(translations: AppTranslation[]) {
  const writeTranslations = await editorMessage(
    MESSAGE_TYPE.WriteTranslations,
    { translations: translations },
  );
  return translations;
}

export async function getEditorData(dataType: MESSAGE_DATA_TYPE): Promise<any> {
  const editorData = await editorMessage(MESSAGE_TYPE.EditorData, {
    dataType: dataType,
  });
  return editorData;
}

export async function getEditorAppId(): Promise<string | null> {
  const content = await editorMessage(MESSAGE_TYPE.EditorAppId);
  return content.appId;
}

export async function getEditorOptionSets(): Promise<OptionSet[]> {
  const optionSetContent: any = await getEditorData(
    MESSAGE_DATA_TYPE.OptionSet,
  );

  const optionSets = optionSetContent.data?.data;

  if (optionSets) {
    return Object.entries(optionSets).map(([id, optionSet]: [string, any]) => ({
      id: id,
      key: optionSet['%d'],
      values: optionSet.values
        ? Object.entries(optionSet.values).map(
            ([valueId, value]: [string, any]) => ({
              id: valueId,
              key: value['%d'],
              value: value.value,
              sortOrder: value.sort_order,
            }),
          )
        : [],
    }));
  }

  return [];
}

function getMatchingOptionSet(
  label: string,
  optionSets: OptionSet[],
): OptionSet | null {
  return (
    optionSets.find((optionSet) => {
      return label.includes(`OS_${optionSet.key}`);
    }) || null
  );
}

export async function getEditorAppTexts(
  withOptionSet: boolean,
  languages?: Language[],
  mainLanguage?: Language,
): Promise<AppText[]> {
  let appTexts: AppText[] = [];

  try {
    let optionSets: OptionSet[] = [];
    if (withOptionSet) {
      optionSets = await getEditorOptionSets();
    }

    const settingsContent: any = await getEditorData(
      MESSAGE_DATA_TYPE.Settings,
    );
    const settings = settingsContent.data;

    if (settings) {
      const appTextsObject = settings.data.client_safe.user_text_ids;

      // Define app texts
      if (appTextsObject) {
        Object.entries(appTextsObject).forEach(([id, label]: [string, any]) => {
          const optionSet = getMatchingOptionSet(label, optionSets);

          if (optionSet) {
            optionSet.values.forEach((optionSetValue) => {
              appTexts.push({
                reference: `${id}_${optionSetValue.id}`,
                id: id,
                key: label,
                translations: {},
                isOptionSet: true,
                optionSetId: optionSet.id,
                optionSetKey: optionSet.key,
                optionSetValueId: optionSetValue.id,
                optionSetValueKey: optionSetValue.key,
              });
            });
          } else {
            appTexts.push({
              reference: id,
              id: id,
              key: label,
              translations: {},
              isOptionSet: false,
            });
          }
        });
      }

      const translationsObject = settings.data.client_safe['%3'];

      appTexts.forEach((appText) => {
        languages?.forEach((language) => {
          let translation: AppTranslation = {
            reference: `${appText.id}_${language.code}${
              appText.isOptionSet ? '_' + appText.optionSetValueId : ''
            }`,
            id: `${appText.id}_${language.code}${
              appText.isOptionSet ? '_' + appText.optionSetValueId : ''
            }`,
            appTextReference: appText.reference,
            appTextId: appText.id,
            languageCode: language.code,
            value: '',
            isOptionSet: appText.isOptionSet,
            optionSetId: appText.optionSetId,
            optionSetKey: appText.optionSetKey,
            optionSetValueId: appText.optionSetValueId,
            optionSetValueKey: appText.optionSetValueKey,
          };

          if (translationsObject) {
            const bubbleTranslation =
              translationsObject[language.code]?.['USER_TEXTS']?.[appText.id];
            if (bubbleTranslation) {
              if (appText.isOptionSet) {
                translation.value =
                  bubbleTranslation.split(
                    `[${appText.optionSetValueKey}]`,
                  )[1] || '';
              } else {
                translation.value = bubbleTranslation;
              }
            }
          }

          appText.translations[language.code] = translation;
        });
      });

      return appTexts;
    } else {
      return [];
    }
  } catch (error) {
    throw error;
  }
}
